<!--
  The conclusion of a deal activity in two steps and one submit (E4 plan AD-02).
  MatDialog is the shell; everything inside is Hyper markup. Remix icons only.
-->
<div class="modal-header">
  <h4 class="modal-title">
    <i class="ri-checkbox-circle-line me-1"></i>
    {{ "task.conclude.title" | translate }}
  </h4>
  <button
    type="button"
    class="btn-close"
    (click)="close()"
    [attr.aria-label]="'task.conclude.cancel' | translate"
  ></button>
</div>

<div class="modal-body">
  @if (loading) {
    <div class="text-center py-4">
      <div class="spinner-border spinner-border-sm text-primary" role="status">
        <span class="visually-hidden">
          {{ "task.conclude.loading" | translate }}
        </span>
      </div>
    </div>
  } @else {
    <p class="text-muted mb-2">
      {{ "task.conclude.subtitle" | translate: { task: taskTitle } }}
    </p>

    <!-- Step indicator: number + text, never colour alone (RNF-08). -->
    <ol
      class="list-unstyled d-flex flex-wrap align-items-center gap-3 mb-3 conclude-steps"
    >
      <li
        class="d-inline-flex align-items-center gap-1"
        [class.fw-bold]="step === 1"
        [attr.aria-current]="step === 1 ? 'step' : null"
      >
        <span
          class="badge rounded-pill"
          [ngClass]="step === 1 ? 'bg-primary' : 'bg-soft-primary text-primary'"
          >1</span
        >
        {{ "task.conclude.stepOutcome" | translate }}
      </li>
      <li class="text-muted" aria-hidden="true">
        <i class="ri-arrow-right-s-line"></i>
      </li>
      <li
        class="d-inline-flex align-items-center gap-1"
        [class.fw-bold]="step === 2"
        [attr.aria-current]="step === 2 ? 'step' : null"
      >
        <span
          class="badge rounded-pill"
          [ngClass]="step === 2 ? 'bg-primary' : 'bg-soft-primary text-primary'"
          >2</span
        >
        {{ "task.conclude.stepNext" | translate }}
        @if (step === 2 && staysOpen) {
          <span
            class="badge campaign-badge ms-1"
            [ngClass]="required ? 'is-warn' : 'is-neutral'"
            [attr.aria-label]="
              (required
                ? 'task.conclude.stepStateRequired'
                : 'task.conclude.stepStateOptional'
              ) | translate
            "
          >
            {{
              (required
                ? "task.conclude.stepStateRequired"
                : "task.conclude.stepStateOptional"
              ) | translate
            }}
          </span>
        }
      </li>
    </ol>

    <!-- ========================= Step 1: the outcome ========================= -->
    @if (step === 1) {
      <form [formGroup]="outcomeForm">
        <div class="row">
          <div class="col-md-6 mb-3">
            <label class="form-label" for="concludeOutcome">{{
              "task.conclude.outcome" | translate
            }}</label>
            <select
              id="concludeOutcome"
              class="form-select"
              formControlName="outcome"
            >
              @for (option of outcomes; track option.value) {
                <option [value]="option.value">
                  {{ option.label | translate }}
                </option>
              }
            </select>
          </div>
          <div class="col-md-6 mb-3">
            <label class="form-label" for="concludeDuration">{{
              "task.conclude.duration" | translate
            }}</label>
            <input
              id="concludeDuration"
              type="number"
              min="0"
              step="1"
              class="form-control"
              formControlName="durationMinutes"
            />
          </div>
        </div>

        <div class="mb-3">
          <label class="form-label d-block">
            {{ "task.conclude.moment" | translate }}
          </label>
          <!-- An interaction happened at a time: "sem horário" is hidden and the date is capped at today (RF-07). -->
          <app-schedule-moment-field
            formControlName="moment"
            [allowUntimed]="false"
            [maxDate]="maxDate"
            idPrefix="concludeMoment"
          />
          <span class="help-block"
            ><small class="text-muted">
              {{ "task.conclude.momentHelp" | translate }}
            </small></span
          >
        </div>

        <div class="mb-3">
          <label class="form-label" for="concludeNote">
            {{ "task.conclude.note" | translate }}
          </label>
          <textarea
            id="concludeNote"
            class="form-control"
            rows="2"
            formControlName="note"
            [placeholder]="'task.conclude.notePlaceholder' | translate"
          ></textarea>
        </div>

        <!-- Optional move and value (RF-10), collapsed by default. -->
        <button
          type="button"
          class="btn btn-link p-0 text-decoration-none d-inline-flex align-items-center gap-1 mb-2"
          (click)="toggleMove()"
          [attr.aria-expanded]="moveOpen"
          aria-controls="concludeMoveSection"
        >
          <i
            [class]="
              moveOpen ? 'ri-arrow-down-s-line' : 'ri-arrow-right-s-line'
            "
          ></i>
          {{ "task.conclude.moveSection" | translate }}
        </button>

        @if (moveOpen) {
          <div id="concludeMoveSection" class="row">
            <div class="col-md-7 mb-3">
              <label class="form-label" for="concludeStage">
                {{ "task.conclude.moveTo" | translate }}
              </label>
              <select
                id="concludeStage"
                class="form-select"
                formControlName="idStage"
                (change)="onStageChange($any($event.target).value)"
              >
                <option value="">
                  {{ "task.conclude.keepStage" | translate }}
                  @if (currentStageName) {
                    ({{ currentStageName }})
                  }
                </option>
                @for (stage of stageOptions; track stage._id) {
                  <option [value]="stage._id">{{ stage.name }}</option>
                }
              </select>
              @if (lossReason) {
                <span class="help-block"
                  ><small class="text-muted"
                    ><i class="ri-check-line me-1"></i
                    >{{ "task.conclude.lossReasonChosen" | translate }}</small
                  ></span
                >
              }
              @if (presentedAt) {
                <span class="help-block"
                  ><small class="text-muted"
                    ><i class="ri-check-line me-1"></i
                    >{{ "task.conclude.presentedAtChosen" | translate }}</small
                  ></span
                >
              }
            </div>
            <div class="col-md-5 mb-3">
              <label class="form-label" for="concludeBudget">
                {{ "task.conclude.budget" | translate }}
              </label>
              <input
                id="concludeBudget"
                type="number"
                min="0"
                step="0.01"
                class="form-control"
                formControlName="budget"
              />
            </div>
          </div>
        }
      </form>
    }

    <!-- ========================= Step 2: the next step ========================= -->
    @if (step === 2) {
      @if (!staysOpen) {
        <div class="alert alert-info d-flex align-items-center" role="alert">
          <i class="ri-information-line me-2 font-18"></i>
          <span>{{ "task.conclude.closedHint" | translate }}</span>
        </div>
      } @else {
        @if (previewFailed) {
          <div
            class="alert alert-secondary d-flex align-items-center"
            role="alert"
          >
            <i class="ri-error-warning-line me-2 font-18"></i>
            <span>{{ "task.conclude.previewFailed" | translate }}</span>
          </div>
        } @else {
          <div
            class="alert d-flex align-items-center"
            [ngClass]="required ? 'alert-warning' : 'alert-info'"
            role="alert"
          >
            <i
              [class]="
                required ? 'ri-alarm-warning-line' : 'ri-information-line'
              "
              class="me-2 font-18"
            ></i>
            <span>{{
              (required ? "task.conclude.required" : "task.conclude.offered")
                | translate
            }}</span>
          </div>
        }

        <!-- What to do next: schedule, skip (only when required), or nothing (only when optional). -->
        <div class="btn-group mb-3" role="group">
          @if (!required) {
            <button
              type="button"
              class="btn"
              [ngClass]="
                mode === 'none' ? 'btn-primary' : 'btn-outline-primary'
              "
              (click)="setMode('none')"
            >
              <i class="ri-check-line me-1"></i
              >{{ "task.conclude.finish" | translate }}
            </button>
          }
          <button
            type="button"
            class="btn"
            [ngClass]="
              mode === 'schedule' ? 'btn-primary' : 'btn-outline-primary'
            "
            (click)="setMode('schedule')"
          >
            <i class="ri-calendar-event-line me-1"></i
            >{{
              (required
                ? "task.conclude.schedule"
                : "task.conclude.scheduleAnother"
              ) | translate
            }}
          </button>
          @if (required) {
            <button
              type="button"
              class="btn"
              [ngClass]="
                mode === 'skip' ? 'btn-primary' : 'btn-outline-primary'
              "
              (click)="setMode('skip')"
            >
              <i class="ri-skip-forward-line me-1"></i
              >{{ "task.conclude.skip" | translate }}
            </button>
          }
        </div>

        @if (mode === "schedule") {
          @if (preview?.suggestion) {
            <p class="text-muted font-13 mb-2">
              <i class="ri-lightbulb-line me-1"></i
              >{{
                "task.conclude.suggestion"
                  | translate: { rule: preview.suggestion.rule }
              }}
            </p>
          }
          <app-next-activity-form [form]="nextForm" idPrefix="concludeNext" />
        }

        @if (mode === "skip") {
          <div class="mb-3">
            <label class="form-label" for="concludeSkipReason">{{
              "task.conclude.skipReason" | translate
            }}</label>
            <textarea
              id="concludeSkipReason"
              class="form-control"
              rows="2"
              [formControl]="skipReason"
            ></textarea>
            @if (skipReason.touched && skipReason.invalid) {
              <span class="help-block text-danger"
                ><small>{{
                  "task.conclude.skipReasonRequired" | translate
                }}</small></span
              >
            }
          </div>
        }
      }
    }
  }
</div>

<div class="modal-footer">
  <button
    type="button"
    class="btn btn-light"
    (click)="close()"
    [disabled]="saving"
  >
    {{ "task.conclude.cancel" | translate }}
  </button>
  @if (step === 1) {
    <button
      type="button"
      class="btn btn-primary"
      (click)="goNext()"
      [disabled]="loading || previewing"
    >
      @if (previewing) {
        <span
          class="spinner-border spinner-border-sm me-1"
          role="status"
        ></span>
      }
      {{ "task.conclude.next" | translate }}
      <i class="ri-arrow-right-line ms-1"></i>
    </button>
  } @else {
    <button
      type="button"
      class="btn btn-outline-secondary"
      (click)="back()"
      [disabled]="saving"
    >
      <i class="ri-arrow-left-line me-1"></i
      >{{ "task.conclude.back" | translate }}
    </button>
    <button
      type="button"
      class="btn btn-primary"
      (click)="finish()"
      [disabled]="saving"
    >
      @if (saving) {
        <span
          class="spinner-border spinner-border-sm me-1"
          role="status"
        ></span>
      }
      {{ "task.conclude.finish" | translate }}
    </button>
  }
</div>
